import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

// Hooks
import { usePayPlan } from "@PayPlans/hooks/usePayPlans/usePayPlans";

// Layouts
import CustomerSatisfactionLoader from "@Layouts/components/Skeletons/CustomerSatisfactionLoader/CustomerSatisfactionLoader";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import TabSelectable from "@Layouts/components/Tab/TabSelectable";

// Components
import MetricsLayer from "./MetricsLayer";
import Weighting from "@PayPlans/components/Weighting";

// MUI
import { Grid } from "@mui/material";

const getMakes = (makeMetrics = [], excludesMakes = []) => {
	const makes = makeMetrics.filter((element) => element.make && !excludesMakes.includes(element.make)).map((item) => item.make);
	return [...new Set(makes)];
};

const getErrorOnEmptyMetrics = (makeMetrics = [], useOnlyStoreMetrics = false) => {
	if (makeMetrics?.length) {
		if (useOnlyStoreMetrics) {
			return makeMetrics.every((element) => !element?.storeMakeMetrics?.metrics?.length);
		}
		return makeMetrics.every((element) => !element?.individualMakeMetrics?.metrics?.length);
	}
	return false;
};

function renderContent(props) {
	const {
		sectionId = "customerSatisfaction",
		weightLabel = "units sold",
		isService = false,
		content = null,
		makes = [],
		readOnly = false,
		useOnlyIndividualMetrics = false,
		useOnlyStoreMetrics = false,
		handleUpdate = () => {},
		handleValidation = () => {},
	} = props;

	let render = [];
	const emptyMetrics = getErrorOnEmptyMetrics(content?.makeMetrics || [], useOnlyStoreMetrics);

	if (content) {
		if (content?.makeMetrics?.length) {
			const filteredMetrics = makes
				.filter((make) => content.makeMetrics.find((element) => element.make === make))
				.map((make) => content.makeMetrics.find((element) => element.make === make));

			render = filteredMetrics.map((metrics, key) => (
				<Grid container key={`${metrics.make}_${key}`}>
					<Grid item xs={12}>
						<MetricsLayer
							make={metrics.make}
							oemReference={metrics?.oemReference ?? ""}
							version={metrics?.version ?? ""}
							isService={isService}
							metrics={metrics}
							emptyError={emptyMetrics}
							useOnlyIndividualMetrics={useOnlyIndividualMetrics}
							useOnlyStoreMetrics={useOnlyStoreMetrics}
							readOnly={readOnly}
							onActionUpdate={(makeMetrics) => handleUpdate(sectionId, metrics.make, makeMetrics)}
							onActionValidation={(value) => handleValidation(sectionId, metrics.make, value)}
						/>
					</Grid>
				</Grid>
			));
		}

		if (makes?.length > 1) {
			render.push(
				<Grid container key={`weighting`}>
					<Grid item xs={12}>
						<Weighting
							makes={makes}
							weighting={content.weighting}
							weightLabel={weightLabel}
							isService={isService}
							readOnly={readOnly}
							onActionUpdate={(weighting) => handleUpdate(sectionId, "weighting", weighting)}
							onActionValidation={(value) => handleValidation(sectionId, "weighting", value)}
						/>
					</Grid>
				</Grid>
			);
		}
	}

	return render;
}
renderContent.propTypes = {
	sectionId: PropTypes.string,
	weightLabel: PropTypes.string,
	isService: PropTypes.bool,
	content: PropTypes.object,
	makes: PropTypes.array,
	readOnly: PropTypes.bool,
	useOnlyIndividualMetrics: PropTypes.bool,
	useOnlyStoreMetrics: PropTypes.bool,
	handleUpdate: PropTypes.func,
	handleValidation: PropTypes.func,
};

export default function CustomerSatisfaction(props) {
	const {
		sectionId = "customerSatisfaction",
		weightLabel = "units sold",
		content = {
			makeMetrics: [],
			weighting: {
				weightingType: "UNITS",
				customWeights: [],
			},
		},
		excludesMakes = ["Cadillac"],
		errors = [],
		isService = false,
		loading = false,
		readOnly = false,
		useOnlyIndividualMetrics = false,
		useOnlyStoreMetrics = false,
		onActionUpdate = () => {},
	} = props;

	// Hooks
	const { useSectionsErrors } = usePayPlan();
	const { getErrorKey, addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors();

	const makes = useMemo(() => getMakes(content?.makeMetrics || [], excludesMakes), [content, excludesMakes]);

	// States
	const [model, setModel] = useState({
		tabsProps: { tabs: [] },
		content: null,
	});

	// Special empty state for errors
	const emptyMetrics = getErrorOnEmptyMetrics(model?.content?.makeMetrics || [], useOnlyStoreMetrics);

	// Error handler
	const tabsErrors = useMemo(() => {
		const sendErrors = [...makes].map((item) => errors?.includes(getErrorKey(sectionId, item)) || emptyMetrics);
		if (makes.length > 1) {
			const weighting = errors?.includes(getErrorKey(sectionId, "weighting"));
			sendErrors.push(weighting);
		}
		return sendErrors;
	}, [sectionId, makes, errors, emptyMetrics, getErrorKey]);

	const handleUpdate = (section, area, data) => {
		let updatedContent = { ...model.content };

		if (area === "weighting") {
			updatedContent = { ...content, [area]: data };
		} else {
			updatedContent?.makeMetrics?.forEach((makeMetric, key) => {
				if (makeMetric.make === area) {
					updatedContent["makeMetrics"][key] = data;
				}
			});
		}

		setModel({ ...model, content: updatedContent });
		onActionUpdate?.(section, area, data);
	};

	useEffect(() => {
		emptyMetrics ? addSectionError(sectionId, "emptyModels") : removeSectionError(sectionId, "emptyModels");
	}, [sectionId, emptyMetrics, addSectionError, removeSectionError]);

	const handleValidation = (section, area, value) => {
		value ? removeSectionError(section, area) : addSectionError(section, area);
	};

	useEffect(() => {
		const tabsToUse = [...makes];
		// Add weighting
		if (content?.weighting && tabsToUse.length > 1) {
			tabsToUse.push("Weights");
		}
		setModel({ content, tabsProps: { tabs: tabsToUse } });

		return () => {
			setModel({
				tabsProps: { tabs: [] },
				content: null,
			});
		};
	}, [content, makes]);

	useEffect(() => {
		return () => {
			clearSectionErrors(sectionId);
		};
	}, [sectionId, clearSectionErrors]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid mt={2}>
						<TabLoader />
						<CustomerSatisfactionLoader title buttons inputs switch sx={{ padding: "0 32px" }} />
					</Grid>
				) : (
					!!model?.content && (
						<TabSelectable
							tabs={model?.tabsProps.tabs || []}
							errors={tabsErrors}
							content={renderContent({
								content: model.content,
								sectionId,
								weightLabel,
								makes,
								isService,
								readOnly,
								useOnlyIndividualMetrics,
								useOnlyStoreMetrics,
								handleUpdate,
								handleValidation,
							})}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
CustomerSatisfaction.propTypes = {
	sectionId: PropTypes.string,
	weightLabel: PropTypes.string,
	content: PropTypes.shape({
		makeMetrics: PropTypes.array,
		weighting: PropTypes.shape({
			weightingType: PropTypes.string,
			customWeights: PropTypes.array,
		}),
	}),
	excludesMakes: PropTypes.array,
	errors: PropTypes.array,
	isService: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useOnlyIndividualMetrics: PropTypes.bool,
	useOnlyStoreMetrics: PropTypes.bool,
	onActionUpdate: PropTypes.func,
};
